import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function SendVerseDialog({ open, onOpenChange, kids, book, ch, verses, refText, text, onSent }: { open: boolean; onOpenChange: (o: boolean) => void; kids: any[]; book: number; ch: number; verses: number[]; refText: string; text: string; onSent?: () => void }) {
  const { user } = useAuth();
  const [picked, setPicked] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [sign, setSign] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPicked(kids.length === 1 ? [kids[0].id] : []); setMessage(""); setSign((user?.name || "").split(" ")[0]); } }, [open, kids, user]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deep/send-verse", { book, ch, verses, child_ids: picked, message, sign });
      toast.success(`Sent to ${data.sent_to.join(" and ")}'s sticker book`);
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="send-verse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-3xl"><Gift className="h-6 w-6 text-[#E07A5F]" />Send to Kids Cove</DialogTitle>
          <DialogDescription>It lands in their sticker book as a verse sticker. Your private notes are never sent.</DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl bg-[#FFF8E8] p-4"><p className="font-scripture text-lg italic text-slate-800 line-clamp-4">“{text}”</p><p className="mt-1 text-sm font-semibold text-amber-800">{refText}</p></div>
        <div className="flex flex-wrap gap-2" data-testid="send-verse-kids">
          {kids.map((k) => {
            const on = picked.includes(k.id);
            return <button key={k.id} data-testid={`send-verse-kid-${k.id}`} aria-pressed={on} onClick={() => setPicked((p) => (on ? p.filter((x) => x !== k.id) : [...p, k.id]))}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${on ? "border-[#E07A5F] bg-[#E07A5F] text-white" : "border-[#E2DBCE] bg-white text-slate-700"}`}>{k.name}</button>;
          })}
        </div>
        <Textarea data-testid="send-verse-message" value={message} maxLength={200} onChange={(e) => setMessage(e.target.value)} placeholder="A little message, like “I love you, keep this close”" className="min-h-[80px]" />
        <div className="flex items-center gap-3"><span className="text-sm font-semibold text-slate-600">From</span><Input data-testid="send-verse-sign" value={sign} maxLength={40} onChange={(e) => setSign(e.target.value)} placeholder="Mom, Dad, Grandma…" /></div>
        <Button data-testid="send-verse-submit" disabled={busy || !picked.length} onClick={send} className="rounded-full bg-[#E07A5F] hover:bg-[#c9664c]">{busy ? "Sending…" : "Send verse sticker"}</Button>
      </DialogContent>
    </Dialog>
  );
}
